import React, { useEffect, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { IoIosArrowRoundBack, IoMdEye } from "react-icons/io";
import { MdFilterAlt } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Pagination from "../../../../components/common/Pagination";
import { FiEdit, FiTrash } from "react-icons/fi";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  confirmTableReservation,
  getAllReservations,
  getReservationDetail,
  cancelBooking,
  deleteBooking,
} from "../../../../api/owner/restaurants/reservations";
import { getTablesByRestaurant } from "../../../../api/owner/restaurants/tables";
import socket from "../../../../helper/socket";
import moment from "moment";
import { useTranslation } from "../../../../hooks/useTranslation";

// Popup Modal Component
const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[978px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const ForRestaurants = () => {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const statuses = [
    "Pending",
    "Awaiting Payment",
    "Confirmed",
    "Cancelled",
    "Rejected",
    "Placed",
  ];
  
  // Map status values to translation keys
  const getStatusTranslation = (statusValue) => {
    if (!statusValue) return t("restaurantreservation").statuses.all;
    const statusMap = {
      "Pending": t("restaurantreservation").statuses.pending,
      "Awaiting Payment": t("restaurantreservation").statuses.awaitingPayment,
      "Confirmed": t("restaurantreservation").statuses.confirmed,
      "Cancelled": t("restaurantreservation").statuses.cancelled,
      "Rejected": t("restaurantreservation").statuses.rejected,
      "Placed": t("restaurantreservation").statuses.placed,
    };
    return statusMap[statusValue] || statusValue;
  };
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 10;
  const navigate = useNavigate();
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  // const [restaurantId, setRestaurantId] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    tableNo: "",
    discountPercentage: 0,
    tokenCharges: "",
  });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["reservations", page, limit, status, search],
    queryFn: () => getAllReservations({ page, limit, status, search }),
    keepPreviousData: true,
  });

  const { data: OrdersDetailData, isLoading: isOrderDetailLoading } = useQuery({
    queryKey: ["reservations-detail", selectedReservationId],
    queryFn: () => getReservationDetail(selectedReservationId),
    enabled: !!selectedReservationId && isPopupOpen,
    keepPreviousData: true,
  });
  const restaurantId = OrdersDetailData?.data?.reservation?.restaurantId?._id;

  const { data: tablesData, isLoading: isTablesDataLoading } = useQuery({
    queryKey: ["tables", restaurantId],
    queryFn: () => getTablesByRestaurant(restaurantId),
    enabled: !!restaurantId,
    keepPreviousData: true,
  });

  const tables = tablesData?.data?.tables;
  const orders = data?.data?.reservations || [];
  const pagination = data?.data?.pagination || {};
  const orderDetail = OrdersDetailData?.data?.reservation;

  const handleCloseModal = () => {
    setIsPopupOpen(false);
    // Clear errors and reset form data when closing modal
    setErrors({});
    setFormData({
      tableNo: "",
      discountPercentage: 0,
      tokenCharges: "",
    });
    setEnabled(false);
  };

  const openModel = (id) => {
    setSelectedReservationId(id);
    setIsPopupOpen(true);
    // Clear errors when opening modal
    setErrors({});
  };

  const updateMutation = useMutation({
    mutationFn: (data) => confirmTableReservation(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["reservations-detail"]);
      toast.success(t("restaurantreservation").success.updated);
      handleCloseModal();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to update. Please try again.";
      toast.error(errorMessage);
    },
  });

  const cancelBookingMutation = useMutation({
    mutationFn: (data) => cancelBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["reservations-detail"]);
      toast.success(t("restaurantreservation").success.cancelled);
      handleCloseModal();
      Swal.close();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to cancel. Please try again.";
      toast.error(errorMessage);
    },
  });

  const deleteBookingMutation = useMutation({
    mutationFn: (data) => deleteBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["reservations-detail"]);
      toast.success(t("restaurantreservation").success.deleted);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete. Please try again.";
      toast.error(errorMessage);
    },
  });


  useEffect(() => {
    const handleNewTableBooking = () => {
      refetch();
    };

    socket.on("tableBooking", handleNewTableBooking);

    return () => {
      socket.off("tableBooking", handleNewTableBooking);
    };
  }, [refetch]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500); // 500ms delay

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // const handleFilterChange = (value) => {
  //   setStatus(value);
  //   setIsOpen(false);
  // };

  const handleFilterChange = (selectedStatus) => {
    setStatus(selectedStatus);
    setIsOpen(false);
    setPage(1);
    refetch();
  };

  const validateForm = (formData, enabled) => {
    const newErrors = {};
    if (formData.discountPercentage === "") {
      newErrors.discountPercentage = t("restaurantreservation").errors.discountRequired;
    } else if (isNaN(formData.discountPercentage)) {
      newErrors.discountPercentage = t("restaurantreservation").errors.discountNumber;
    } else if (
      formData.discountPercentage < 0 ||
      formData.discountPercentage > 100
    ) {
      newErrors.discountPercentage = t("restaurantreservation").errors.discountRange;
    }

    if (orderDetail?.restaurantId?.tableBookingEnabled) {
      if (formData.tableNo === "") {
        newErrors.tableNo = t("restaurantreservation").errors.tableRequired;
      } else if (isNaN(formData.tableNo)) {
        newErrors.tableNo = t("restaurantreservation").errors.tableNumber;
      }
    }

    if (enabled) {
      if (formData.tokenCharges === "") {
        newErrors.tokenCharges = t("restaurantreservation").errors.tokenRequired;
      } else if (isNaN(formData.tokenCharges)) {
        newErrors.tokenCharges = t("restaurantreservation").errors.tokenNumber;
      } else if (formData.tokenCharges < 0) {
        newErrors.tokenCharges = t("restaurantreservation").errors.tokenNegative;
      }
    }

    return newErrors;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    const validationErrors = validateForm(formData, enabled);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const data = {
      tokenCharges: formData.tokenCharges === "" ? 0 : formData.tokenCharges,
      tableNo: formData.tableNo,
      discountPercentage: formData.discountPercentage,
    };

    updateMutation.mutate({ reservationId: selectedReservationId, data });
  };

  const handleCancel = () => {
    Swal.fire({
      title: t("restaurantreservation").confirmation.cancelTitle,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("restaurantreservation").confirmation.cancelConfirm,
      cancelButtonText: t("restaurantreservation").confirmation.cancelText,
    }).then((result) => {
      if (result.isConfirmed) {
        cancelBookingMutation.mutate({ reservationId: selectedReservationId });
      }
    });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t("restaurantreservation").confirmation.deleteTitle,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("restaurantreservation").confirmation.deleteConfirm,
      cancelButtonText: t("restaurantreservation").confirmation.deleteText,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteBookingMutation.mutate({ reservationId: id });
      }
    });
  };

  function convertToAmPm(timeStr) {
    if (!timeStr || typeof timeStr !== "string" || !timeStr.includes(":"))
      return "";

    const [hour, minute] = timeStr.split(":").map(Number);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
  }

  const bookingDate = orderDetail?.bookingDate;
  const endTime = orderDetail?.slot?.endTime;

  let isExpired = false;

  if (bookingDate && endTime) {
    const [hour, minute] = endTime.split(":");
    const endDateTime = moment(bookingDate).set({
      hour: +hour,
      minute: +minute,
      second: 0,
      millisecond: 0,
    });
    isExpired = moment().isAfter(endDateTime);
  }

  return (
    <>
      <div className="flex justify-between items-center flex-wrap gap-[20px]">
        <div className="flex gap-[10px] items-center">
          <div className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]">
            <IoIosArrowRoundBack
              onClick={() => navigate(-1)}
              size={24}
              className="text-[#fff]"
            />
          </div>
          <h2 className="text-[22px] font-medium">
            {t("restaurantreservation").restaurantReservations}
          </h2>
        </div>
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="py-[9px] px-[35px] bg-white text-base  font-medium flex  items-center gap-[6px] rounded-[2px] border border-[#DDDDDD]"
          >
            <MdFilterAlt /> {status ? getStatusTranslation(status) : t("restaurantreservation").filter}
          </button>

          {isOpen && (
            <div className="absolute z-10 mt-2 w-full bg-white border rounded shadow text-sm font-medium">
              {/* All statuses option */}
              <div
                onClick={() => handleFilterChange("")}
                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                  status === "" ? "bg-gray-200 font-semibold" : ""
                }`}
              >
                {t("restaurantreservation").statuses.all}
              </div>

              {/* Map statuses */}
              {statuses.map((item) => (
                <div
                  key={item}
                  onClick={() => handleFilterChange(item)}
                  className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                    status === item ? "bg-gray-200 font-semibold" : ""
                  }`}
                >
                  {getStatusTranslation(item)}
                </div>
              ))}
            </div>
          )}
          {/* {isOpen && (
            <div className="absolute z-10 mt-2 w-36 bg-white border rounded shadow text-sm font-medium">
              {statuses.map((item) => (
                <div
                  key={item}
                  onClick={() => handleFilterChange(item)}
                  className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                >
                  {item}
                </div>
              ))}
            </div>
          )} */}
        </div>
      </div>
      <div className="mt-[31px]">
        <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden mt-[28px] py-[20px] sm:px-[30px] px-[15px] bg-white grid">
          <div className="flex justify-between items-center py-[13px] flex-wrap  gap-[10px]">
            <h2 className="text-base font-semibold">
              {t("restaurantreservation").reservationList}
            </h2>
            <div className="relative w-full max-w-[301px]">
              <span className="absolute inset-y-0 left-3 flex items-center">
                <CiSearch size={24} className="text-[#4CAF50]" />
              </span>
              <input
                type="text"
                placeholder={t("restaurantreservation").search}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full border border-[#DDDDDD] focus:outline-none focus:border-[#4CAF50]"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : isError ? (
            <p className="text-red-500 text-center mt-6">{error.message}</p>
          ) : (
            <div className="overflow-x-auto md:mt-[30px] mt-[20px]">
              <table className="min-w-full  ">
                <thead className="bg-[#FFFAF2] ">
                  <tr className="">
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("restaurantreservation").bookingId}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("restaurantreservation").restaurantName}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("restaurantreservation").customer}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("restaurantreservation").totalGuest}
                    </th>

                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("restaurantreservation").foodType}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] max-w-[197px] whitespace-nowrap">
                      {t("restaurantreservation").slotTime}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("restaurantreservation").mealType}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("restaurantreservation").bookingDate}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] max-w-[168px] whitespace-nowrap">
                      {t("restaurantreservation").payment}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("restaurantreservation").status}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px]  whitespace-nowrap">
                      {t("restaurantreservation").tokenCharge}
                    </th>

                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("restaurantreservation").actions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders?.length > 0 ? (
                    orders?.map((order) => {
                      return (
                        <tr className="border-t border-[#E4E4E4] ">
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.reservationId}
                          </td>
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.restaurantId?.name}
                          </td>
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.userId?.name}
                          </td>
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.totalGuest}
                          </td>

                          <td className="py-[20px] px-[16px] ">
                            {order?.foodType}
                          </td>
                          <td className="py-[20px] px-[16px] text-base text-black  whitespace-nowrap">
                            {convertToAmPm(order?.slot?.startTime)} -{" "}
                            {convertToAmPm(order?.slot?.endTime)}
                          </td>
                          <td className="py-[20px] px-[16px] ">
                            {order?.mealType}
                          </td>
                          <td className="py-[20px] px-[16px]">
                            {order?.bookingDate &&
                              new Date(order.bookingDate).toLocaleDateString(
                                "en-GB"
                              )}
                          </td>
                          <td className="py-[20px] px-[16px] ">
                            {order?.tokenPaymentDetails?.paymentGateway
                              ? order?.tokenPaymentDetails?.paymentGateway
                              : "-"}
                          </td>
                          <td className="py-[20px] px-[16px]">
                            <div
                              className={`px-[28px] py-2 w-full font-normal rounded-full text-center text-sm 
      ${
        order?.status?.toLowerCase() === "pending" ||
        order?.status?.toLowerCase() === "awaiting payment"
          ? "bg-yellow-500 text-white"
          : order?.status?.toLowerCase() === "confirmed"
          ? "bg-[#4CAF50] text-white"
          : order?.status?.toLowerCase() === "cancelled"
          ? "bg-red-500 text-white"
          : "bg-gray-300 text-black"
      }
    `}
                            >
                              {order?.status}
                            </div>
                          </td>

                          <td className="py-[20px] px-[16px] ">
                            <div
                              // onClick={() => setEnabled(!enabled)}
                              className={`w-[48px] h-[20px] mx-auto flex items-center rounded-full p-1 cursor-pointer transition-colors ${
                                order?.tokenCharges > 0
                                  ? "bg-[#4CAF50]"
                                  : "bg-[#e1e1e1]"
                              }`}
                            >
                              <div
                                className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${
                                  order?.tokenCharges > 0
                                    ? "translate-x-6"
                                    : "translate-x-0"
                                }`}
                              />
                            </div>
                          </td>
                          <td className="py-[20px] px-[16px]  flex gap-[12px]">
                            <a
                              href="#"
                              onClick={() => openModel(order?._id)}
                              className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                            >
                              <IoMdEye className="text-base text-black" />
                            </a>

                            <button
                              onClick={() => handleDelete(order?._id)}
                              className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                            >
                              <RiDeleteBin6Line className="text-base text-black" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="12">
                        <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                          <div className="w-14 h-14 rounded-full bg-[#E7F4ED] flex items-center justify-center mb-4">
                            <HiOutlineOfficeBuilding className="text-[#4CAF50] text-2xl" />
                          </div>
                          <p className="text-lg font-semibold text-[#111827]">
                            {t("restaurantreservation").noReservationsFound}
                          </p>
                          <p className="text-sm text-[#6B7280] mt-2 max-w-[360px]">
                            {t("restaurantreservation").noReservationsHint}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {orders?.length > 0 && (
            <Pagination
              page={pagination.currentPage}
              totalPages={pagination.totalPages}
              setPage={setPage}
            />
          )}
        </div>
      </div>

      <PopupModal isOpen={isPopupOpen} onClose={handleCloseModal}>
        <div className="w-full rounded-[20px] md:p-[30px] py-[20px] px-[15px]">
          <div className="grid grid-col-1 sm:grid-cols-3 justify-between text-left pb-[21px] border-b border-[#EEEEEE] mb-[18px]">
            <h2 className="text-[22px] font-medium col-span-2">
              {t("restaurantreservation").reservationDetail}
            </h2>

            {orderDetail?.status === "pending" && (
              <div className="flex items-center gap-[20px]">
                <h2 className="text-lg font-medium">
                  {t("restaurantreservation").tokenCharges}
                </h2>
                <div
                  onClick={() => setEnabled(!enabled)}
                  className={`w-[48px] h-[20px] flex items-center rounded-full p-1 cursor-pointer transition-colors ${
                    enabled ? "bg-blue-500" : "bg-[#e1e1e1]"
                  }`}
                >
                  <div
                    className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${
                      enabled ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="h-[350px] overflow-y-auto">
            <p className="text-[17px] font-medium mb-[15px]">
              {t("restaurantreservation").bookingId}:
              {orderDetail?.reservationId} - {orderDetail?.userId?.name}'s {""}
              {t("restaurantreservation").resrvation}
            </p>
            <div className="border border-[#DDDDDD] rounded-[5px] xl:gap-[47px] gap-[20px] p-[20px] pb-[36px] bg-white grid lg:grid-cols-[auto_auto_1fr] md:grid-cols-2 grid-cols-1">
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("restaurantreservation").reservationInformation}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("restaurantreservation").bookingId}:{""}
                    </span>
                    {orderDetail?.reservationId}
                  </li>

                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("restaurantreservation").name}:{""}
                    </span>
                    {orderDetail?.userId?.name}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("restaurantreservation").totalGuest}:{""}
                    </span>
                    {orderDetail?.totalGuest}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("restaurantreservation").foodType}:{""}
                    </span>{" "}
                    {orderDetail?.foodType}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("restaurantreservation").mealType}:{""}
                    </span>
                    {orderDetail?.mealType}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("restaurantreservation").slotTime}:{""}
                    </span>{" "}
                    {convertToAmPm(orderDetail?.slot?.startTime)} -{" "}
                    {convertToAmPm(orderDetail?.slot?.endTime)}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {" "}
                      {t("restaurantreservation").bookingDate}:{""}
                    </span>
                    {orderDetail?.bookingDate &&
                      new Date(orderDetail.bookingDate).toLocaleDateString(
                        "en-GB"
                      )}
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("restaurantreservation").paymentInformation}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("restaurantreservation").paymentMethod}:{""}
                    </span>
                    {orderDetail?.tokenPaymentDetails?.paymentGateway}
                  </li>
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("restaurantreservation").paymentStatus}:{""}
                    </span>{" "}
                    {orderDetail?.isPaid ? "Paid" : null}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("restaurantreservation").amount}:{""}
                    </span>{" "}
                    ₾{orderDetail?.tokenCharges}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {" "}
                      {t("restaurantreservation").discountPercentage}:{""}
                    </span>{" "}
                    {orderDetail?.discountPercentage}%
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("restaurantreservation").reservationStatus}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("restaurantreservation").currentStatus}:{""}
                    </span>{" "}
                    {orderDetail?.status}
                  </li>
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("restaurantreservation").tableCode}:{""}
                    </span>{" "}
                    {orderDetail?.tableCode}
                  </li>
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("restaurantreservation").tableNumber}:{""}
                    </span>{" "}
                    {orderDetail?.tableNo}
                  </li>
                  {/* <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">Action Required:</span>{" "}
                    Payment confirmation
                  </li> */}
                </ul>
              </div>
            </div>

            {orderDetail?.status === "pending" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-[20px] mt-5">
                  <div className="max-w-[473px]">
                    <h2 className="text-[17px] font-medium mb-[10px]">
                      {t("restaurantreservation").assignDiscount}
                    </h2>
                    <p className="text-[15px] text-[#605D5D] font-normal">
                      {t("restaurantreservation").assignDiscountDesc}
                    </p>
                  </div>
                  <div className="w-full sm:max-w-[324px] sm:ml-auto">
                    <h2 className="text-[17px] font-medium mb-[10px]">
                      {t("restaurantreservation").discountPercentage}
                    </h2>
                    <div className="flex items-center sm:flex-row flex-col gap-[10px]">
                      <div className="flex border border-[#DDDDDD] rounded-md overflow-hidden w-full sm:max-w-[184px]">
                        <div className="flex items-center justify-center px-3 border-r border-[#DDDDDD] text-[#18181B] text-lg">
                          %
                        </div>
                        <input
                          type="number"
                          name="discountPercentage"
                          value={formData.discountPercentage}
                          onChange={(e) =>
                            handleChange("discountPercentage", e.target.value)
                          }
                          className={`border rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50]`}
                          placeholder={t("restaurantreservation").placeholder}
                        />
                      </div>
                    </div>
                    {errors.discountPercentage && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.discountPercentage}
                      </p>
                    )}
                  </div>
                </div>

                {orderDetail?.restaurantId?.tableBookingEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-[20px] mt-5">
                    <div className="max-w-[473px]">
                      <h2 className="text-[17px] font-medium mb-[10px]">
                        {t("restaurantreservation").assignTable}
                      </h2>
                    </div>
                    <div className="w-full sm:max-w-[324px] sm:ml-auto">
                      <h2 className="text-[17px] font-medium mb-[10px]">
                        {t("restaurantreservation").tableNo}
                      </h2>
                      <div className="flex items-center sm:flex-row flex-col gap-[10px]">
                        <div className="flex border border-[#DDDDDD] rounded-md overflow-hidden w-full sm:max-w-[184px]">
                          <select
                            name="tableNo"
                            value={formData.tableNo}
                            onChange={(e) =>
                              handleChange("tableNo", e.target.value)
                            }
                            className="border rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50]"
                          >
                            <option value="">
                              {t("restaurantreservation").selectTable}
                            </option>
                            {tables?.map((table) => (
                              <option
                                key={table?._id}
                                value={table?.tableNumber}
                              >
                                {table?.tableNumber} with {table?.chairs} chairs
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {errors.tableNo && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.tableNo}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-[20px] mt-5">
                    <div className="max-w-[473px]">
                      <h2 className="text-[17px] font-medium mb-[10px]">
                        {t("restaurantreservation").tokenCharges}
                      </h2>
                      <p className="text-[15px] text-[#605D5D] font-normal">
                        {t("restaurantreservation").tokenChargesDesc}
                      </p>
                    </div>
                    <div className="w-full sm:max-w-[324px] sm:ml-auto">
                      <h2 className="text-[17px] font-medium mb-[10px]">
                        {t("restaurantreservation").amount}
                      </h2>
                      <div className="flex items-center sm:flex-row flex-col gap-[10px]">
                        <div className="flex border border-[#DDDDDD] rounded-md overflow-hidden w-full sm:max-w-[184px]">
                          <div className="flex items-center justify-center px-3 border-r border-[#DDDDDD] text-[#18181B] text-lg">
                            ₾
                          </div>
                          <input
                            type="number"
                            value={formData.tokenCharges}
                            onChange={(e) =>
                              handleChange("tokenCharges", e.target.value)
                            }
                            className={`border rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50]`}
                            placeholder={t("restaurantreservation").placeholder}
                          />
                        </div>
                      </div>
                      {errors.tokenCharges && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.tokenCharges}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex sm:flex-row flex-col md:gap-[30px] gap-[10px] justify-end pt-[23px] mt-[30px] border-t border-[#EEEEEE]">
                  <button
                    onClick={handleSubmit}
                    className="inline-block sm:w-max w-full rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold py-[14px] px-[27px] green-shadow hover:bg-[#209b25]"
                  >
                    {t("restaurantreservation").confirmBooking}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="text-white  sm:w-max w-full font-bold inline-block py-[14px] px-[15px] rounded-[2px] bg-[#FF0000] hover:bg-[#9B2020] 2xl:text-[20px] text-base text-center red-shadow"
                  >
                    {t("restaurantreservation").cancelReservation}
                  </button>
                </div>
              </>
            )} 
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default ForRestaurants;
